import React, { useEffect, useState, useCallback } from "react";
import { useUserForm } from "../hooks/useUserForm";
import { useAssignmentSearch } from "../hooks/useAssignmentSearch";
import { userService } from "../services/userService";
import UserImageUpload from "./UserImageUpload";
import "../../NewUpload/report-styles/customSelect.scss";
import {
  CUSTOMER_SUPPORT_ROLE,
  getDisplayUserType,
  shouldShowManagerType,
  shouldShowSupportLevel,
  shouldShowOperationsManagerFields,
  getManagerTypeOptions,
  getAssignmentFieldName,
  getVerificationIdFieldLabel,
  hasRequiredUserFormFields,
} from "../utils/userHelper";
import {
  SUPPORT_LEVEL_OPTIONS,
  VERIFICATION_TYPE_OPTIONS,
  LEAVE_DAYS_OPTIONS,
} from "../utils/userConfigs";
import UserCircleIcon from "../../../img/account_circle.svg";
import PhoneIcon from "../../../img/phone.svg";
import EmailIcon from "../../../img/mail.svg";
import UsersIcon from "../../../img/users.svg";
import LockOpenIcon from "../../../img/lock_open.svg";
import CustomSelect from "../../Shared/CustomSelect/CustomSelect";
import PersonPinIcon from "../../../img/person_pin.svg";
import BadgeNidIcon from "../../../img/badge_nid.svg";
import IdBadgeIcon from "../../../img/id_badge.svg";
import { useNotification } from "../../Notifications/NotificationProvider";
import FormField from "../../Shared/Form/FormField";

const UserFormModal = ({
  showUserCreateModal,
  setShowUserCreateModal,
  onUserCreated,
  isEditMode = false,
  editUserData = null,
  isEditDataLoading = false,
  onModalClose,
  prefilledUserType = "",
  roleOptions = [],
}) => {
  const authUserType = JSON.parse(localStorage.getItem("currUserType")) || "";
  const [isCreating, setIsCreating] = useState(false);
  const dispatchNotification = useNotification();
  const {
    form,
    formErrors,
    generalError,
    handleInputChange,
    handleMultiselectChange,
    handleImageChange,
    validateForm,
    resetForm,
    setFormData,
    setApiErrors,
    setGeneralError,
  } = useUserForm();

  const { assignedToOptions } = useAssignmentSearch(form.user_type);

  const assignmentFieldName = getAssignmentFieldName(form.user_type);
  const verificationIdLabel = getVerificationIdFieldLabel(
    form.verification_type
  );

  const handleAssignmentChange = useCallback(
    (e) => {
      const selectedIds = Array.isArray(e.target.value)
        ? e.target.value
        : [e.target.value];
      const selectedOptions = assignedToOptions
        .filter(
          (option) =>
            !option.isPlaceholder && selectedIds.includes(option.value)
        )
        .map((option) => ({
          id: option.value,
          name: option.label,
          value: option.value,
          label: option.label,
        }));

      handleMultiselectChange(assignmentFieldName, selectedOptions);
    },
    [assignedToOptions, handleMultiselectChange, assignmentFieldName]
  );

  const handleImageChangeWithNotification = useCallback(
    (file) => {
      if (file) {
        const maxSize = 1 * 1024 * 1024;
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ];

        if (file.size > maxSize) {
          const errorMessage = "Image size must be less than 1MB";
          dispatchNotification({
            type: "error",
            message: errorMessage,
          });
          return;
        }

        if (!allowedTypes.includes(file.type)) {
          const errorMessage =
            "Image must be in JPEG, PNG, GIF, or WebP format";
          dispatchNotification({
            type: "error",
            message: errorMessage,
          });
          return;
        }
      }

      handleImageChange(file);
    },
    [handleImageChange, dispatchNotification]
  );

  const customSelectItem = {
    options: assignedToOptions
      .filter((option) => !option.isPlaceholder)
      .map((option) => ({
        id: option.value,
        name: option.label,
      })),
    value:
      assignmentFieldName && Array.isArray(form[assignmentFieldName])
        ? form[assignmentFieldName].map((item) => item.value || item.id)
        : [],
    icon: UsersIcon,
  };

  const roleCustomSelectItem = {
    options: roleOptions.map((option) => ({
      id: option.value,
      name: option.name,
    })),
    value: form.user_type ? [getDisplayUserType(form.user_type)] : [],
    icon: UsersIcon,
  };

  const managerTypeCustomSelectItem = {
    options: getManagerTypeOptions(form.user_type).map((option) => ({
      id: option.value,
      name: option.label,
    })),
    value: form.user_category ? [form.user_category] : [],
    icon: UsersIcon,
  };

  const supportLevelCustomSelectItem = {
    options: SUPPORT_LEVEL_OPTIONS.filter(
      (option) => option.value !== authUserType
    ).map((option) => ({
      id: option.value,
      name: option.label,
    })),
    value: form.support_level ? [form.support_level] : [],
    icon: UsersIcon,
  };

  const leaveDaysCustomSelectItem = {
    options: LEAVE_DAYS_OPTIONS.map((option) => ({
      id: option.value,
      name: option.label,
    })),
    value: form.leave_day ? [form.leave_day] : [],
    icon: BadgeNidIcon,
  };

  const verificationTypeCustomSelectItem = {
    options: VERIFICATION_TYPE_OPTIONS.map((option) => ({
      id: option.value,
      name: option.label,
    })),
    value: form.verification_type ? [form.verification_type] : [],
    icon: IdBadgeIcon,
  };

  const transformEditDataToFormFormat = useCallback((data) => {
    if (!data) return data;
    const assignmentField = getAssignmentFieldName(data.user_type);
    let assignmentValue = [];
    if (data[assignmentField] && Array.isArray(data[assignmentField])) {
      assignmentValue = data[assignmentField]
        .filter(
          (item) =>
            item &&
            typeof item === "object" &&
            item.id !== "" &&
            item.id !== null &&
            item.id !== undefined
        )
        .map((item) => ({
          id: item.id,
          name: item.name,
          value: item.id,
          label: item.name,
        }));
    }
    let { joining_date } = data;
    if (!joining_date || joining_date === null) {
      joining_date = new Date().toISOString().split("T")[0];
    } else if (joining_date.includes("T")) {
      joining_date = joining_date.split("T")[0];
    }

    let verification_type = "";
    let verification_id = "";
    let user_type = data.user_type || "";
    let support_level = data.support_level || "";
    if (
      data.verification &&
      Array.isArray(data.verification) &&
      data.verification.length > 0
    ) {
      verification_type = data.verification[0].type || "";
      verification_id = data.verification[0].value || "";
    }

    if (getDisplayUserType(user_type) === CUSTOMER_SUPPORT_ROLE) {
      support_level = user_type;
      user_type = CUSTOMER_SUPPORT_ROLE;
    }

    let contact_number = data.contact_number || "";
    let personal_number = data.personal_number || "+88";

    if (
      contact_number &&
      !contact_number.startsWith("+88") &&
      !contact_number.startsWith("+")
    ) {
      contact_number = "+88" + contact_number;
    }

    if (
      personal_number &&
      !personal_number.startsWith("+88") &&
      !personal_number.startsWith("+")
    ) {
      personal_number = "+88" + personal_number;
    }

    if (data.image && typeof data.image === "string") {
      data.image = {
        url: data.image,
        preview: data.image,
      };
    }

    const result = {
      ...data,
      [assignmentField]: assignmentValue,
      joining_date,
      name: data.name || "",
      user_type,
      support_level,
      verification_type,
      verification_id,
      contact_number,
      personal_number,
    };
    return result;
  }, []);

  useEffect(() => {
    if (showUserCreateModal && isEditMode && editUserData) {
      const transformedData = transformEditDataToFormFormat(editUserData);
      setFormData(transformedData);
    }
  }, [
    showUserCreateModal,
    isEditMode,
    editUserData,
    setFormData,
    transformEditDataToFormFormat,
  ]);

  useEffect(() => {
    if (showUserCreateModal && !isEditMode) {
      resetForm();
    }
  }, [showUserCreateModal, isEditMode, resetForm]);

  useEffect(() => {
    if (
      showUserCreateModal &&
      !isEditMode &&
      prefilledUserType &&
      !form.user_type
    ) {
      const normalizedUserType = getDisplayUserType(prefilledUserType);
      setFormData({
        user_type: normalizedUserType,
        support_level:
          normalizedUserType === CUSTOMER_SUPPORT_ROLE ? prefilledUserType : "",
      });
    }
  }, [
    showUserCreateModal,
    isEditMode,
    prefilledUserType,
    form.user_type,
    setFormData,
  ]);

  useEffect(() => {
    if (showUserCreateModal && !isEditMode && form.user_type) {
      const allAssignmentFields = [
        "cluster",
        "region",
        "area",
        "territory",
        "agency",
      ];
      const currentAssignmentField = getAssignmentFieldName(form.user_type);

      const fieldsToUpdate = {};
      allAssignmentFields.forEach((field) => {
        fieldsToUpdate[field] =
          field === currentAssignmentField ? [] : undefined;
      });

      setFormData(fieldsToUpdate);
    }
  }, [form.user_type, showUserCreateModal, isEditMode, setFormData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm({ isEditMode })) {
      const firstErrorField = Object.keys(formErrors)[0];
      const firstErrorMessage = formErrors[firstErrorField]?.[0];

      if (firstErrorMessage) {
        dispatchNotification({
          type: "ERROR",
          message: firstErrorMessage,
        });
      } else {
        dispatchNotification({
          type: "ERROR",
          message: "Please fill in all required fields correctly",
        });
      }
      return;
    }

    setIsCreating(true);
    setGeneralError("");

    try {
      const formData = userService.buildFormData(form);

      let response;
      if (isEditMode) {
        response = await userService.updateUser(formData);
      } else {
        response = await userService.createUser(formData);
      }

      dispatchNotification({
        type: "SUCCESS",
        message: `User ${isEditMode ? "updated" : "created"} successfully!`,
      });
      resetForm();
      setShowUserCreateModal(false);
      if (onModalClose) {
        onModalClose();
      }
      if (onUserCreated) {
        onUserCreated(response.data || response.user);
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setApiErrors(error.response.data);
        dispatchNotification({
          type: "ERROR",
          message:
            error.response.data.message ||
            `Failed to ${isEditMode ? "update" : "create"} user`,
        });
      } else if (error.message) {
        setGeneralError(error.message);
        dispatchNotification({
          type: "ERROR",
          message: error.message,
        });
      } else {
        const errorMessage = `An error occurred while ${isEditMode ? "updating" : "creating"} the user`;
        setGeneralError(errorMessage);
        dispatchNotification({
          type: "error",
          message: errorMessage,
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      resetForm();
      setShowUserCreateModal(false);
      if (onModalClose) {
        onModalClose();
      }
    }
  };

  if (!showUserCreateModal) return null;

  const shouldShowFormSkeleton = isEditMode && isEditDataLoading;
  const isSubmitDisabled =
    isCreating ||
    shouldShowFormSkeleton ||
    (!isEditMode && !hasRequiredUserFormFields(form));

  const renderFormSkeleton = () => (
    <div className="user-form-skeleton">
      <div className="user-form-skeleton-image" />
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={`form-skeleton-${index}`} className="user-form-skeleton-row">
          <div className="user-form-skeleton-label" />
          <div className="user-form-skeleton-input" />
        </div>
      ))}
    </div>
  );

  const renderFieldError = (fieldName) => {
    if (
      !fieldName ||
      !formErrors[fieldName] ||
      formErrors[fieldName].length === 0
    ) {
      return null;
    }

    return <div className="field-error">{formErrors[fieldName][0]}</div>;
  };

  const renderFieldGroup = (fieldName, content) => (
    <div className="user-form-field-group">
      {content}
      {renderFieldError(fieldName)}
    </div>
  );

  return (
    <div className="user-modal-overlay">
      <form
        className="user-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="user-modal-header">
          <h3>{isEditMode ? "Edit User" : "Add New User"}</h3>
          <button
            className="close-btn"
            type="button"
            onClick={handleClose}
            disabled={isCreating}
          >
            ×
          </button>
        </div>

        <div className="user-modal-body">
          {shouldShowFormSkeleton ? (
            renderFormSkeleton()
          ) : (
            <div className="user-form-container">
              {renderFieldGroup(
                "image",
                <UserImageUpload
                  image={form.image}
                  onImageChange={handleImageChangeWithNotification}
                  disabled={isCreating}
                />
              )}

              {renderFieldGroup(
                "username",
                <FormField
                  id="user-name"
                  name="username"
                  title="User Name"
                  type="text"
                  icon={UserCircleIcon}
                  value={form.username}
                  onChange={handleInputChange}
                  placeholder="Enter user name..."
                  disabled={isCreating}
                  required
                />
              )}

              {renderFieldGroup(
                "name",
                <FormField
                  id="full-name"
                  name="name"
                  title="Full Name"
                  type="text"
                  icon={UserCircleIcon}
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name..."
                  disabled={isCreating}
                  required
                />
              )}

              {renderFieldGroup(
                "contact_number",
                <FormField
                  id="contact-number"
                  name="contact_number"
                  title="Phone Number"
                  type="tel"
                  icon={PhoneIcon}
                  value={form.contact_number}
                  onChange={handleInputChange}
                  placeholder="Enter phone number (+88XXXXXXXXXXX)..."
                  disabled={isCreating}
                  required
                />
              )}

              {renderFieldGroup(
                "email",
                <FormField
                  id="email"
                  name="email"
                  title="Email Address"
                  type="email"
                  icon={EmailIcon}
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address..."
                  disabled={isCreating}
                  required
                />
              )}

              {renderFieldGroup(
                "password",
                <FormField
                  id="password"
                  name="password"
                  title="Set Password"
                  type="password"
                  viewPassword
                  value={form.password}
                  onChange={handleInputChange}
                  placeholder="Enter password (8-50 characters)..."
                  icon={LockOpenIcon}
                  disabled={isCreating}
                  required={!isEditMode}
                />
              )}

              {renderFieldGroup(
                "confirm_password",
                <FormField
                  id="confirm-password"
                  name="confirm_password"
                  title="Retype Password"
                  type="password"
                  viewPassword
                  value={form.confirm_password}
                  onChange={handleInputChange}
                  placeholder="Re-enter password..."
                  icon={LockOpenIcon}
                  disabled={isCreating}
                  required={!isEditMode}
                />
              )}

              {renderFieldGroup(
                "user_type",
                <div className="common-form-item">
                  <label htmlFor="user-type" className="common-form-label">
                    Role <span style={{ color: "#362B73" }}> *</span>
                  </label>
                  <div className="custom-select-wrapper">
                    <CustomSelect
                      item={roleCustomSelectItem}
                      isMultiSelect={false}
                      handleChange={(e) => {
                        const selectedUserType = e.target.value;
                        handleInputChange({
                          target: {
                            name: "user_type",
                            value: selectedUserType,
                          },
                        });

                        if (selectedUserType !== CUSTOMER_SUPPORT_ROLE) {
                          handleInputChange({
                            target: {
                              name: "support_level",
                              value: "",
                            },
                          });
                        }
                      }}
                      showExpandIndicator={true}
                      placeholder="Select Role"
                      style={{
                        backgroundColor: "#ffffffb8",
                        border: "2px solid #ffffffb8",
                        transition: "border-color 0.2s ease",
                        boxShadow: "none",
                      }}
                      isDisabled={isCreating}
                      isSearchable={false}
                    />
                  </div>
                </div>
              )}

              {assignmentFieldName &&
                renderFieldGroup(
                  assignmentFieldName,
                  <div className="common-form-item">
                    <label
                      htmlFor="assignment-field"
                      className="common-form-label"
                    >
                      Assigned to <span style={{ color: "#362B73" }}> *</span>
                    </label>
                    <div className="custom-select-wrapper">
                      <CustomSelect
                        item={customSelectItem}
                        isMultiSelect={true}
                        handleChange={handleAssignmentChange}
                        showExpandIndicator={true}
                        style={{
                          backgroundColor: "#ffffffb8",
                          border: "2px solid #ffffffb8",
                          transition: "border-color 0.2s ease",
                          boxShadow: "none",
                        }}
                        isDisabled={isCreating || !form.user_type}
                      />
                    </div>
                  </div>
                )}

              {shouldShowManagerType(form.user_type) &&
                renderFieldGroup(
                  "user_category",
                  <div className="common-form-item">
                    <label htmlFor="manager-type" className="common-form-label">
                      Manager Type <span style={{ color: "#362B73" }}> *</span>
                    </label>
                    <div className="custom-select-wrapper">
                      <CustomSelect
                        item={managerTypeCustomSelectItem}
                        isMultiSelect={false}
                        handleChange={(e) => {
                          handleInputChange({
                            target: {
                              name: "user_category",
                              value: e.target.value,
                            },
                          });
                        }}
                        showExpandIndicator={true}
                        placeholder="Select Manager Type"
                        style={{
                          background: "#ffffffb8",
                          border: "2px solid #ffffffb8",
                          transition: "border-color 0.2s ease",
                          boxShadow: "none",
                        }}
                        isDisabled={isCreating}
                        isSearchable={false}
                      />
                    </div>
                  </div>
                )}

              {shouldShowSupportLevel(form.user_type) &&
                renderFieldGroup(
                  "support_level",
                  <div className="common-form-item">
                    <label
                      htmlFor="support-level"
                      className="common-form-label"
                    >
                      Support Level <span style={{ color: "#362B73" }}> *</span>
                    </label>
                    <div className="custom-select-wrapper">
                      <CustomSelect
                        item={supportLevelCustomSelectItem}
                        isMultiSelect={false}
                        handleChange={(e) => {
                          handleInputChange({
                            target: {
                              name: "support_level",
                              value: e.target.value,
                            },
                          });
                        }}
                        showExpandIndicator={true}
                        placeholder="Select Support Level"
                        style={{
                          background: "#ffffffb8",
                          border: "2px solid #ffffffb8",
                          transition: "border-color 0.2s ease",
                          boxShadow: "none",
                        }}
                        isDisabled={isCreating}
                        isSearchable={false}
                      />
                    </div>
                  </div>
                )}

              {shouldShowOperationsManagerFields(form.user_type) && (
                <>
                  {renderFieldGroup(
                    "leave_day",
                    <div className="common-form-item">
                      <label htmlFor="leave-days" className="common-form-label">
                        Leave Days <span style={{ color: "#362B73" }}> *</span>
                      </label>
                      <div className="custom-select-wrapper">
                        <CustomSelect
                          item={leaveDaysCustomSelectItem}
                          isMultiSelect={false}
                          handleChange={(e) => {
                            handleInputChange({
                              target: {
                                name: "leave_day",
                                value: e.target.value,
                              },
                            });
                          }}
                          showExpandIndicator={true}
                          placeholder="Select Leave Day"
                          style={{
                            background: "#ffffffb8",
                            border: "2px solid #ffffffb8",
                            transition: "border-color 0.2s ease",
                            boxShadow: "none",
                          }}
                          isDisabled={isCreating}
                          isSearchable={false}
                        />
                      </div>
                    </div>
                  )}

                  {renderFieldGroup(
                    "personal_number",
                    <FormField
                      id="personal-bkash-number"
                      name="personal_number"
                      title="Personal bKash Number"
                      type="tel"
                      icon={PhoneIcon}
                      value={form.personal_number}
                      onChange={handleInputChange}
                      placeholder="Enter personal bKash number (+88XXXXXXXXXXX)..."
                      disabled={isCreating}
                    />
                  )}

                  {renderFieldGroup(
                    "father_name",
                    <FormField
                      id="fathers-name"
                      name="father_name"
                      title="Father's Name"
                      type="text"
                      icon={PersonPinIcon}
                      value={form.father_name}
                      onChange={handleInputChange}
                      placeholder="Enter father's name (max 50 chars)..."
                      disabled={isCreating}
                    />
                  )}

                  {renderFieldGroup(
                    "mother_name",
                    <FormField
                      id="mothers-name"
                      name="mother_name"
                      title="Mother's Name"
                      type="text"
                      icon={PersonPinIcon}
                      value={form.mother_name}
                      onChange={handleInputChange}
                      placeholder="Enter mother's name (max 50 chars)..."
                      disabled={isCreating}
                    />
                  )}

                  {renderFieldGroup(
                    "verification_type",
                    <div className="common-form-item">
                      <label
                        htmlFor="verification-type"
                        className="common-form-label"
                      >
                        Verification Type{" "}
                        <span style={{ color: "#362B73" }}> *</span>
                      </label>
                      <div className="custom-select-wrapper">
                        <CustomSelect
                          item={verificationTypeCustomSelectItem}
                          isMultiSelect={false}
                          handleChange={(e) => {
                            handleInputChange({
                              target: {
                                name: "verification_type",
                                value: e.target.value,
                              },
                            });
                          }}
                          showExpandIndicator={true}
                          placeholder="Select Verification Type"
                          style={{
                            background: "#ffffffb8",
                            border: "2px solid #ffffffb8",
                            transition: "border-color 0.2s ease",
                            boxShadow: "none",
                          }}
                          isDisabled={isCreating}
                          isSearchable={false}
                        />
                      </div>
                    </div>
                  )}

                  {renderFieldGroup(
                    "verification_id",
                    <FormField
                      id="verification-id"
                      name="verification_id"
                      title={verificationIdLabel}
                      type="text"
                      icon={BadgeNidIcon}
                      value={form.verification_id}
                      onChange={handleInputChange}
                      placeholder={`Enter ${verificationIdLabel.toLowerCase()} (max 50 chars)...`}
                      disabled={isCreating}
                      required
                    />
                  )}
                </>
              )}

              {renderFieldGroup(
                "joining_date",
                <FormField
                  id="joining-date"
                  name="joining_date"
                  title="Joining Date"
                  type="date"
                  value={form.joining_date}
                  onChange={handleInputChange}
                  disabled={isCreating}
                  required
                />
              )}

              {generalError && (
                <div className="user-form-error">{generalError}</div>
              )}
            </div>
          )}
        </div>

        <div className="user-modal-footer">
          <button
            className="cancel-btn"
            type="button"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            className="submit-btn"
            type="submit"
            disabled={isSubmitDisabled}
          >
            {isCreating
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update User"
                : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserFormModal;
